<template>
  <form
    class="form-time-range"
    v-on:submit.prevent="onSubmit"
  >
    <div class="form-time-range__wrapper-input">
      <time-autocomplete
        class="form-time-range__input"
        placeholder="С"
        v-bind:timeline="fromTimeline"
        hide-details
        v-model="from" />
      —
      <time-autocomplete
        class="form-time-range__input"
        placeholder="По"
        v-bind:timeline="toTimeline"
        hide-details
        v-model="to" />
    </div>
    <button
      v-if="buttonClean && isVisibleButtonClean"
      v-on:click="onResetTime"
      class="form-time-range__clean"
      type="button"
      title="Очистить время"
    />
    <p
      class="form-time-range__violations"
      role="alert"
      v-for="(violationText, index) in violations"
      v-bind:key="index"
    >{{ violationText }}</p>
  </form>
</template>

<script>
import TimeAutocomplete from '@/components/TimeAutocomplete.vue';

/**
 * @description Компонент формы временного диапазона дня.
 * @type {Vue}
 */
export default {
  name: 'FormTimeRange',
  placeholderValue: '00:00',
  maskTime: '##:##',
  components: {
    TimeAutocomplete,
  },
  data() {
    return {
      from: this.initialFrom || this.$options.placeholderValue,
      to: this.initialTo || this.$options.placeholderValue,
    };
  },
  computed: {
    /**
     * @description Временная шкала для выбора начала времени.
     * @type {{title: string, datetime: string}[]}
     */
    fromTimeline() {
      const inputs = this.presetFromTimeline.length > 0
        ? this.presetFromTimeline.slice()
        : this.timeline.slice();
      if (this.from && this.addInitial) {
        inputs.unshift({ datetime: '', title: this.from });
      }
      return inputs;
    },
    /**
     * @description Видимость кнопки очистки времени.
     * @type {boolean}
     */
    isVisibleButtonClean() {
      return this.initialFrom || this.initialTo;
    },
    /**
     * @description Временная шкала для выбора окончания времени.
     * @type {{title: string, datetime: string}[]}
     */
    toTimeline() {
      const inputs = this.presetToTimeline.length > 0
        ? this.presetToTimeline.slice()
        : this.timeline.slice();
      if (this.to && this.addInitial) {
        inputs.unshift({ datetime: '', title: this.to });
      }
      return inputs.concat(this.timeline.filter((item) => item.title > this.from));
    },
  },
  methods: {
    /**
     * @description Обработать установку времени
     * @returns {void}
     */
    onSubmit() {
      this.$emit('submit', this.from, this.to);
    },
    /**
     * @description Сбросить время
     * @returns {void}
     */
    onResetTime() {
      this.from = '';
      this.to = '';
    },
    /**
     * @description Сдвинуть значение "до" к следующему большему за данным.
     * @param {string} from Опорное значение.
     * @returns {void}
     */
    shiftToToNextHigherTime(from) {
      if (from) {
        this
          .toTimeline
          .flatMap(({ title }) => (title > from ? [title] : []))
          .sort((value) => Number(value < from))
          .slice(0, 1)
          .forEach((value) => { this.to = value; });
      }
    },
  },
  props: {
    /**
     * @description Время начала.
     * @type {String}
     */
    initialFrom: { type: String, default: '' },
    /**
     * @description Время окончания.
     * @type {String}
     */
    initialTo: { type: String, default: '' },
    /**
     * @description Временная шкала.
     * @type {Any[]}
     */
    timeline: { type: [Object, Array], required: true },
    presetFromTimeline: { default: () => [], type: Array },
    presetToTimeline: { default: () => [], type: Array },
    /**
     * @description Список нарушений (ошибок).
     * @type {String[]}
     */
    violations: { default: () => [], type: Array },
    /**
     * @description Добавлять начальные данные.
     * @type {Boolean}
     */
    addInitial: { default: () => true, type: Boolean },
    /**
     * @description Добавить кнопку очистки времени.
     * @type {Boolean}
     */
    buttonClean: { default: () => false, type: Boolean },
  },
  watch: {
    from(updatedValue) {
      this.shiftToToNextHigherTime(updatedValue);
      this.onSubmit();
    },
    to() {
      this.onSubmit();
    },
    initialFrom(updatedValue) {
      this.from = updatedValue;
    },
    initialTo(updatedValue) {
      this.to = updatedValue;
    },
  },
};
</script>

<style lang="scss" scoped>
$button-font-size: 14px;
$button-height: 37px;
$button-width: 100%;
::v-deep {
  .v-select.v-select--is-menu-active .v-input__icon--append .v-icon:not(.primary--text) {
    transform: rotate(0);
  }
}
.form-time-range {
  display: grid;
  grid-template-columns: minmax(200px, 300px) auto;
  align-items: center;
  gap: 6px 20px;
  max-width: fit-content;
  &__wrapper-input {
    display: grid;
    grid-template-columns: 1fr 10px 1fr;
    align-items: center;
    grid-gap: 10px;
    width: min(100%, 300px);
  }
  &__input {
    margin-block: 2px;
    padding: 0;
    box-sizing: border-box;
    appearance: textfield;
    color: #707683;
    font-size: 16px;
    &:focus {
      color: #192a3e;
      background-color: #fff;
      border: 1px solid #0489e0 !important;
      outline: 0;
    }
    &:disabled {
      background-color: #fff;
    }
  }
  &__clean {
    mask-image: url("@/assets/img/exit-black.svg");
    mask-position: center;
    mask-size: auto;
    mask-repeat: no-repeat;
    background-color: #192A3E;
    width: 10px;
    height: 10px;
    transition: background-color 0.3s;
    cursor: pointer;
    &:hover {
      background-color: var(--action-color);
    }
  }
  &__violations {
    grid-column: span 2;
    font-size: 0.8rem;
    color: var(--warning-color);
  }
}
</style>
